/**
 * ParentDashboard - Parent-facing control center
 * 
 * Features:
 * - AI-generated progress summaries
 * - Assessment upload (i-Ready, MAP Growth, etc.)
 * - Payout approvals
 * - Cost monitoring (Confluent data)
 * - Learning Arc visualization (K-12 progression)
 */

import { useState, useEffect } from 'react';
import { tracker } from '../lib/tracking';
import { 
  getProgressSummary, 
  type ProgressSummary,
  type StudentProfile,
} from '../lib/adaptive';
import { SCENARIOS, type MathLevel, type QuestionLens } from '../lib/scenarios';
import { LearningArc } from '../components/LearningArc';
import { AssessmentUpload } from '../components/AssessmentUpload';

// Types
interface Child {
  id: string;
  name: string;
  robloxUsername: string;
  level: number;
  totalXP: number;
  streak: number;
  lastActive: string;
}

interface PendingPayout {
  id: string;
  childId: string;
  childName: string;
  amount: number;
  challengeTitle: string;
  completedAt: string;
  score: number;
}

interface CostData {
  category: string;
  spent: number;
  limit: number;
  percentUsed: number;
}

// Progress card component
function ProgressCard({ summary, childName }: { summary: ProgressSummary; childName: string }) {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-blox-card to-blox-surface border border-blox-surface p-6">
      {/* Headline */}
      <div className="mb-4">
        <h3 className="text-sm text-slate-400 mb-1">{childName}'s Progress</h3>
        <p className="text-xl font-semibold text-white">{summary.headline}</p>
      </div>

      {/* Celebration moment */}
      {summary.celebrationMoment && (
        <div className="mb-4 p-3 rounded-xl bg-blox-gold/10 border border-blox-gold/30">
          <span className="text-2xl mr-2">🎉</span>
          <span className="text-blox-gold">{summary.celebrationMoment}</span>
        </div>
      )}

      {/* Strengths & Growth */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <h4 className="text-sm font-semibold text-blox-xp mb-2 flex items-center gap-2">
            <span>💪</span> Strengths
          </h4>
          <ul className="space-y-1">
            {summary.strengths.map((s, i) => (
              <li key={i} className="text-sm text-slate-300">{s}</li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-blox-accent mb-2 flex items-center gap-2">
            <span>🌱</span> Growing
          </h4>
          <ul className="space-y-1">
            {summary.growthAreas.map((g, i) => (
              <li key={i} className="text-sm text-slate-300">{g}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Recommended actions */}
      <div className="pt-4 border-t border-blox-surface">
        <h4 className="text-sm font-semibold text-slate-400 mb-2">How you can help:</h4>
        <ul className="space-y-2">
          {summary.recommendedActions.map((action, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
              <span className="text-blox-accent">→</span>
              {action}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Payout approval card
function PayoutCard({ 
  payout, 
  onApprove, 
  onDeny,
  isProcessing,
}: { 
  payout: PendingPayout;
  onApprove: () => void;
  onDeny: () => void;
  isProcessing: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-blox-surface border border-blox-card">
      <div className="flex items-center gap-4">
        {/* Score badge */}
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center font-display text-lg
          ${payout.score >= 0.9 
            ? 'bg-blox-xp/20 text-blox-xp border border-blox-xp/30' 
            : 'bg-blox-gold/20 text-blox-gold border border-blox-gold/30'}
        `}>
          {Math.round(payout.score * 100)}%
        </div>

        {/* Details */}
        <div>
          <div className="font-semibold text-white">{payout.childName}</div>
          <div className="text-sm text-slate-400">
            Completed: {payout.challengeTitle}
          </div>
          <div className="text-xs text-slate-500">
            {new Date(payout.completedAt).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Amount & actions */}
      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="font-display text-2xl text-blox-gold">{payout.amount}</div>
          <div className="text-xs text-slate-500">Robux</div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onApprove}
            disabled={isProcessing}
            className="px-4 py-2 rounded-xl bg-blox-success text-white font-semibold text-sm hover:bg-blox-success/90 disabled:opacity-50 transition-colors"
          >
            Approve
          </button>
          <button
            onClick={onDeny}
            disabled={isProcessing}
            className="px-4 py-2 rounded-xl border border-slate-600 text-slate-400 text-sm hover:bg-blox-surface disabled:opacity-50 transition-colors"
          >
            Deny
          </button>
        </div>
      </div>
    </div>
  );
}

// Cost monitor component
function CostMonitor({ costs }: { costs: CostData[] }) {
  return (
    <div className="rounded-2xl bg-blox-card border border-blox-surface p-6">
      <h3 className="font-display text-lg text-white mb-4 flex items-center gap-2">
        <span>📊</span> API Usage Today
      </h3>

      <div className="space-y-4">
        {costs.map(cost => (
          <div key={cost.category}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-400 capitalize">{cost.category}</span>
              <span className={`font-mono ${
                cost.percentUsed > 80 ? 'text-blox-error' : 
                cost.percentUsed > 60 ? 'text-blox-gold' : 
                'text-slate-300'
              }`}>
                ${cost.spent.toFixed(2)} / ${cost.limit.toFixed(2)}
              </span>
            </div>
            <div className="h-2 bg-blox-dark rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  cost.percentUsed > 80 ? 'bg-blox-error' : 
                  cost.percentUsed > 60 ? 'bg-blox-gold' : 
                  'bg-blox-accent'
                }`}
                style={{ width: `${Math.min(cost.percentUsed, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <p className="mt-4 text-xs text-slate-500">
        Free tier includes basic voice & AI features. Costs reset daily at midnight.
      </p>
    </div>
  );
}

// Child selector
function ChildSelector({ 
  children, 
  selected, 
  onSelect 
}: { 
  children: Child[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {children.map(child => (
        <button
          key={child.id}
          onClick={() => onSelect(child.id)}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-xl whitespace-nowrap transition-all
            ${selected === child.id 
              ? 'bg-blox-accent text-blox-dark' 
              : 'bg-blox-surface text-slate-300 hover:bg-blox-card'}
          `}
        >
          <div className={`
            w-8 h-8 rounded-lg flex items-center justify-center font-display text-sm
            ${selected === child.id ? 'bg-blox-dark/20' : 'bg-blox-card'}
          `}>
            {child.level}
          </div>
          <div className="text-left">
            <div className="font-semibold">{child.name}</div>
            <div className="text-xs opacity-70">
              {child.streak}🔥 • {child.totalXP} XP
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// Main component
export default function ParentDashboard() {
  // Mock data - would come from API
  const [children] = useState<Child[]>([
    {
      id: 'child_1',
      name: 'Flora',
      robloxUsername: 'FloraGamer123',
      level: 7,
      totalXP: 3450,
      streak: 5,
      lastActive: new Date().toISOString(),
    },
    {
      id: 'child_2',
      name: 'Panda',
      robloxUsername: 'PandaPlays',
      level: 4,
      totalXP: 1200,
      streak: 2,
      lastActive: new Date(Date.now() - 86400000).toISOString(),
    },
  ]);

  const [selectedChild, setSelectedChild] = useState(children[0].id);
  const [progressSummary, setProgressSummary] = useState<ProgressSummary | null>(null);
  const [pendingPayouts, setPendingPayouts] = useState<PendingPayout[]>([
    {
      id: 'pay_1',
      childId: 'child_1',
      childName: 'Flora',
      amount: 25,
      challengeTitle: 'Power Rule Sprint',
      completedAt: new Date().toISOString(),
      score: 0.95,
    },
    {
      id: 'pay_2',
      childId: 'child_1',
      childName: 'Flora',
      amount: 50,
      challengeTitle: 'Polynomial Boss Battle',
      completedAt: new Date(Date.now() - 3600000).toISOString(),
      score: 0.85,
    },
  ]);

  const [processingPayout, setProcessingPayout] = useState<string | null>(null);
  const [costs, setCosts] = useState<CostData[]>([]);
  
  // Assessment upload modal state
  const [showAssessmentUpload, setShowAssessmentUpload] = useState(false);

  // Load progress summary when child changes
  useEffect(() => {
    loadProgressSummary();
    loadCosts();
  }, [selectedChild]);

  async function loadProgressSummary() {
    const child = children.find(c => c.id === selectedChild);
    if (!child) return;

    const profile: StudentProfile = {
      userId: child.id,
      isChild: true,
      currentLevel: child.level,
      totalXP: child.totalXP,
      streakDays: child.streak,
      masteredSkills: ['algebra.linear'],
      strugglingSkills: ['calc.power_rule'],
      recentAttempts: [],
    };

    const summary = await getProgressSummary(profile);
    setProgressSummary(summary);
  }

  function loadCosts() {
    const costSummary = tracker.getCostSummary();
    
    setCosts([
      { 
        category: 'voice', 
        spent: costSummary.daily.voice, 
        limit: costSummary.limits.voice,
        percentUsed: costSummary.percentUsed.voice || 0,
      },
      { 
        category: 'ai', 
        spent: costSummary.daily.ai, 
        limit: costSummary.limits.ai,
        percentUsed: costSummary.percentUsed.ai || 0,
      },
    ]);
  }

  async function handleApprovePayout(payoutId: string) {
    setProcessingPayout(payoutId);
    
    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setPendingPayouts(prev => prev.filter(p => p.id !== payoutId));
      
      tracker.trackUser({
        action: 'payout_approved',
        metadata: { payoutId },
      });
    } finally {
      setProcessingPayout(null);
    }
  }

  async function handleDenyPayout(payoutId: string) {
    setProcessingPayout(payoutId);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setPendingPayouts(prev => prev.filter(p => p.id !== payoutId));
      
      tracker.trackUser({
        action: 'payout_denied',
        metadata: { payoutId },
      });
    } finally {
      setProcessingPayout(null);
    }
  }

  function handleAssessmentSuccess(data: unknown) {
    console.log('Assessment uploaded:', data);
    tracker.trackUser({
      action: 'assessment_uploaded',
      metadata: { childId: selectedChild },
    });
    // Could refresh progress summary here
    loadProgressSummary();
  }

  const selectedChildData = children.find(c => c.id === selectedChild);
  const childPayouts = pendingPayouts.filter(p => p.childId === selectedChild);

  return (
    <div className="min-h-screen bg-blox-darker">
      {/* Header */}
      <header className="bg-blox-dark border-b border-blox-surface">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl text-white tracking-wider">
                GOAL<span className="text-blox-accent">BLOX</span>
              </h1>
              <p className="text-sm text-slate-400">Parent Dashboard</p>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-slate-400 text-sm">
                Welcome back 👋
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Child selector */}
        <section className="mb-6">
          <ChildSelector 
            children={children}
            selected={selectedChild}
            onSelect={setSelectedChild}
          />
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Progress Summary */}
            {progressSummary && selectedChildData && (
              <ProgressCard 
                summary={progressSummary} 
                childName={selectedChildData.name}
              />
            )}

            {/* Pending Payouts */}
            <section>
              <h2 className="font-display text-lg text-white mb-4 flex items-center gap-2">
                <span className="text-blox-gold">💰</span> 
                Pending Rewards
                {childPayouts.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-blox-gold/20 text-blox-gold text-sm">
                    {childPayouts.length}
                  </span>
                )}
              </h2>

              {childPayouts.length === 0 ? (
                <div className="p-8 rounded-2xl bg-blox-surface border border-blox-card text-center">
                  <span className="text-4xl mb-3 block">🎮</span>
                  <p className="text-slate-400">No pending rewards right now.</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Rewards appear here when challenges are completed.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {childPayouts.map(payout => (
                    <PayoutCard
                      key={payout.id}
                      payout={payout}
                      onApprove={() => handleApprovePayout(payout.id)}
                      onDeny={() => handleDenyPayout(payout.id)}
                      isProcessing={processingPayout === payout.id}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Quick stats */}
            {selectedChildData && (
              <section className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-blox-surface border border-blox-card text-center">
                  <div className="text-3xl font-display text-white">{selectedChildData.level}</div>
                  <div className="text-sm text-slate-400">Level</div>
                </div>
                <div className="p-4 rounded-xl bg-blox-surface border border-blox-card text-center">
                  <div className="text-3xl font-display text-blox-xp">{selectedChildData.totalXP}</div>
                  <div className="text-sm text-slate-400">Total XP</div>
                </div>
                <div className="p-4 rounded-xl bg-blox-surface border border-blox-card text-center">
                  <div className="text-3xl font-display text-orange-400">{selectedChildData.streak}🔥</div>
                  <div className="text-sm text-slate-400">Day Streak</div>
                </div>
              </section>
            )}
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Cost Monitor */}
            <CostMonitor costs={costs} />

            {/* Quick actions */}
            <div className="rounded-2xl bg-blox-card border border-blox-surface p-6">
              <h3 className="font-display text-lg text-white mb-4">Quick Actions</h3>
              
              <div className="space-y-3">
                <button 
                  onClick={() => setShowAssessmentUpload(true)}
                  className="w-full px-4 py-3 rounded-xl bg-blox-accent text-blox-dark font-semibold text-sm hover:bg-blox-accent/90 transition-colors text-left flex items-center gap-3"
                >
                  <span>📋</span> Upload Assessment
                </button>
                <button className="w-full px-4 py-3 rounded-xl bg-blox-surface text-slate-300 text-sm hover:bg-blox-card transition-colors text-left flex items-center gap-3">
                  <span>📝</span> Assign New Challenge
                </button>
                <button className="w-full px-4 py-3 rounded-xl bg-blox-surface text-slate-300 text-sm hover:bg-blox-card transition-colors text-left flex items-center gap-3">
                  <span>💰</span> Add Robux Budget
                </button>
                <button className="w-full px-4 py-3 rounded-xl bg-blox-surface text-slate-300 text-sm hover:bg-blox-card transition-colors text-left flex items-center gap-3">
                  <span>📊</span> View Full Report
                </button>
                <button className="w-full px-4 py-3 rounded-xl bg-blox-surface text-slate-300 text-sm hover:bg-blox-card transition-colors text-left flex items-center gap-3">
                  <span>⚙️</span> Settings
                </button>
              </div>
            </div>

            {/* Privacy notice */}
            <div className="p-4 rounded-xl bg-blox-surface/50 border border-blox-card">
              <h4 className="text-sm font-semibold text-slate-400 mb-2 flex items-center gap-2">
                <span>🔒</span> Privacy First
              </h4>
              <p className="text-xs text-slate-500">
                All data is COPPA compliant. Children's identities are anonymized 
                in analytics. You control what data is collected.
              </p>
            </div>
          </div>
        </div>

        {/* Learning Arc Section */}
        {selectedChildData && (
          <section className="mt-8">
            <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2">
              <span className="text-blox-accent">📚</span> Learning Journey
            </h2>
            <p className="text-sm text-slate-400 mb-6">
              See how {selectedChildData.name} progresses from basic math to calculus 
              using the <span className="text-white">same familiar game stories</span>.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Object.values(SCENARIOS).slice(0, 2).map(scenario => (
                <LearningArc
                  key={scenario.id}
                  scenarioId={scenario.id}
                  childName={selectedChildData.name}
                  completedQuestions={[
                    // Demo data - would come from API
                    { lens: 'evaluate' as QuestionLens, level: 'elementary' as MathLevel, score: 0.95 },
                    { lens: 'evaluate' as QuestionLens, level: 'pre_algebra' as MathLevel, score: 0.88 },
                    { lens: 'slope' as QuestionLens, level: 'algebra_1' as MathLevel, score: 0.82 },
                  ]}
                />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Assessment Upload Modal */}
      {showAssessmentUpload && selectedChildData && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <AssessmentUpload
            childId={selectedChildData.id}
            childName={selectedChildData.name}
            onSuccess={handleAssessmentSuccess}
            onClose={() => setShowAssessmentUpload(false)}
          />
        </div>
      )}
    </div>
  );
}
